import React from 'react';
import { CheckCircle, Image } from 'lucide-react';
import PRICING from '../config/pricing';

const PhotoGrid = ({
  photos,
  selectedPhotos,
  freeTrialStatus,
  togglePhoto,
  toggleSelectAll,
  selectUntitledPhotos,
  scrollToProcessingOptions
}) => {
  if (photos.length === 0) {
    return null;
  }

  return (
    <div className="bg-white p-6 rounded-lg shadow">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-6 gap-4">
        <div>
          <h2 className="text-xl font-semibold">Photos ({photos.length})</h2>
          <p className="text-sm text-gray-600 mt-1">Click any photo to select it for processing</p>
        </div>
        
        <div className="flex items-center space-x-4">
          <div className={`
            px-3 py-2 rounded-lg text-sm font-medium transition-colors duration-200
            ${selectedPhotos.size > 0 
              ? 'bg-blue-100 text-blue-800 border border-blue-200' 
              : 'bg-gray-100 text-gray-600'
            }
          `}>
            {selectedPhotos.size} of {photos.length} selected
            {freeTrialStatus && freeTrialStatus.isEligible && selectedPhotos.size > 0 && (
              <span className="ml-2">
                {selectedPhotos.size <= freeTrialStatus.remainingPhotos ? (
                  <span className="text-green-600 font-semibold">• Free</span>
                ) : (
                  <span className="text-orange-600 font-semibold">• {PRICING.formatPrice(PRICING.calculateAmountWithFreeTrial(selectedPhotos.size, freeTrialStatus.remainingPhotos))}</span>
                )}
              </span>
            )}
          </div>
          
          <button
            onClick={selectUntitledPhotos}
            disabled={photos.filter(photo => !photo.title || photo.title.trim() === '').length === 0}
            className="bg-amber-600 text-white px-4 py-2 rounded-lg hover:bg-amber-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-sm font-medium transition-colors duration-200"
          >
            Select Untitled ({photos.filter(photo => !photo.title || photo.title.trim() === '').length})
          </button>
          
          <button
            onClick={() => {
              toggleSelectAll();
              // If selecting all, scroll to processing options after a brief delay
              if (selectedPhotos.size !== photos.length && scrollToProcessingOptions) {
                setTimeout(() => {
                  scrollToProcessingOptions();
                }, 100);
              }
            }}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 text-sm font-medium transition-colors duration-200"
          >
            {selectedPhotos.size === photos.length ? 'Deselect All' : 'Select All'}
          </button>
        </div>
      </div>
      
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
        {photos.map(photo => {
          const isSelected = selectedPhotos.has(photo.id);
          return (
            <div 
              key={photo.id} 
              onClick={() => togglePhoto(photo.id)}
              className={`
                relative cursor-pointer rounded-lg overflow-hidden transition-all duration-200 transform hover:scale-105
                ${isSelected 
                  ? 'ring-4 ring-blue-500 ring-opacity-50 shadow-lg border-2 border-blue-500' 
                  : 'border-2 border-gray-200 hover:border-gray-300 hover:shadow-md'
                }
              `}
            >
              {/* Photo Image */}
              <div className="relative aspect-square">
                <img 
                  src={photo.urlSmall || photo.urlMedium} 
                  alt={photo.title}
                  className="w-full h-full object-cover"
                />
                
                {/* Selection Overlay */}
                {isSelected && (
                  <div className="absolute inset-0 bg-blue-600 bg-opacity-20 flex items-center justify-center">
                    <div className="bg-blue-600 rounded-full p-2">
                      <CheckCircle className="h-6 w-6 text-white" />
                    </div>
                  </div>
                )}
              </div>
              
              {/* Photo Info */}
              <div className={`
                p-3 transition-colors duration-200
                ${isSelected ? 'bg-blue-50 border-t border-blue-200' : 'bg-white'}
              `}>
                <p className={`
                  text-sm font-medium transition-colors duration-200 line-clamp-2 leading-tight
                  ${isSelected ? 'text-blue-900' : 'text-gray-900'}
                `}>
                  {photo.title || 'Untitled'}
                </p>
                <p className={`
                  text-xs truncate transition-colors duration-200
                  ${isSelected ? 'text-blue-600' : 'text-gray-500'}
                `}>
                  {photo.dateUploaded ? new Date(photo.dateUploaded).toLocaleDateString() : 'Date unknown'}
                </p>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default PhotoGrid;
